import type { Problem } from './types.ts';

/** An RFC 9457 problem from the API (or a synthesized one for network failures). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: Problem | null;
  readonly retryAfter: number | null;

  constructor(status: number, problem: Problem | null, fallback: string) {
    super(problem?.detail || problem?.title || fallback);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.code = problem?.code ?? (status === 0 ? 'network' : `http_${status}`);
    this.retryAfter = problem?.retryAfter ?? null;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

/** True for failures worth retrying with the same Idempotency-Key. */
export function isRetryable(e: unknown): boolean {
  if (!isApiError(e)) return false;
  return e.isNetwork || e.status === 502 || e.status === 503 || e.status === 504 || e.status === 429;
}

/** User-facing message for an API failure. */
export function apiMessage(e: unknown, fallback: string): string {
  if (!isApiError(e)) return e instanceof Error && e.message ? e.message : fallback;
  if (e.isNetwork) return 'You’re offline or the server can’t be reached. Your work is saved on this device.';
  switch (e.code) {
    case 'insufficient_credits':
      return 'You’re out of AI render credits.';
    case 'member_allowance_used':
      return e.message; // the reset date and who can buy more, from the server
    case 'no_entitlement':
      return 'Your plan doesn’t include this. Upgrade to continue.';
    case 'daily_cap':
      return 'Daily AI render limit reached. Try again tomorrow.';
    case 'too_many_renders':
      return 'Too many renders are running. Wait for one to finish.';
    case 'ai_unavailable':
      return 'AI renders are temporarily unavailable. Please try again later.';
    case 'storage_quota':
      return 'Your cloud storage is full. Projects stay on this device.';
    case 'upgrade_required':
      return 'Update Better Lights to keep syncing.';
    case 'feature_required':
      return 'Your plan doesn’t include this.';
    case 'forbidden_role':
      return 'Your role in this company doesn’t allow this.';
    case 'seat_limit':
      return 'All seats of this company are taken. An owner can add seats under Billing.';
    case 'beta_code_invalid':
      return 'That beta code isn’t valid. Check it and try again.';
    case 'beta_code_expired':
      return 'That beta code has expired.';
    case 'beta_code_used_up':
      return 'That beta code has already been used.';
    case 'beta_already_active':
      return 'You already have an active beta. This code can’t extend it.';
    case 'rate_limited':
      return 'Too many tries. Wait a minute and try again.';
    case 'demo_account':
      return 'Billing is turned off for the demo account.';
    case 'not_configured':
      return 'This isn’t set up on the server yet.';
    default:
      return e.message || fallback;
  }
}

/** The plan feature a 402 `feature_required` asks for, else null. */
export function requiredFeature(e: unknown): string | null {
  return isApiError(e) && e.code === 'feature_required' ? (e.problem?.feature ?? 'sync') : null;
}
